'use client'

import type { CompanyFilingView } from '@/services/filings/types'
import { extractReportType } from '@/utils/filing-utils'
import { GlobalCard, GlobalCardContent } from '../common/global-card'
import { AnalyzeButton } from '../ai-elements/analyze-button'

interface FilingCardProps {
    filing: CompanyFilingView
    onClick: (filing: CompanyFilingView) => void
}

export function FilingCard({ filing, onClick }: FilingCardProps) {
    const reportType = extractReportType(filing.description ?? undefined)
    const displayTitle = reportType || filing.description || `${filing.form_type} Filing`

    // Format the description to split out any SEC items (e.g., "Item 5.02") on new lines.
    const originalDescription = filing.description ?? ''
    const itemMatches = originalDescription.match(/Item\s+\d+(?:\.\d+)?/g) || []
    const parts = originalDescription.split(' - ')

    let baseDescription = originalDescription
    if (itemMatches.length > 0) {
        const formPart = parts[0] ?? ''
        const reportPart = parts[1] ?? ''
        baseDescription = [formPart, reportPart].filter(Boolean).join(' - ')
        if (reportPart) baseDescription += ' - '
    } else {
        if (parts.length >= 2) {
            const cleanedSecond = (parts[1] ?? '').replace(/\s*\[.*?\]\s*$/, '')
            baseDescription = [parts[0], cleanedSecond].filter(Boolean).join(' - ')
        } else {
            baseDescription = originalDescription.replace(/\s*\[.*?\]\s*$/, '')
        }
    }

    return (
        <GlobalCard className=" min-w-0! max-w-full! flex-none!" onClick={() => onClick(filing)}>
            <GlobalCardContent className="p-1.5 h-[214px] flex flex-col justify-between">
                <div className="space-y-1.5">
                    <div className="bg-sidebar text-subheadline-medium rounded-[4px] px-2.5 py-[5px] w-fit">
                        {filing.form_type}
                    </div>
                    <h3 className="text-body-semi text-black/80 dark:text-white/80">
                        {displayTitle}
                    </h3>
                    <div className="text-callout text-black/40 dark:text-white/40 px-0.5">
                        <div>{baseDescription}</div>
                        {itemMatches.length > 0 && (
                            <div className="space-y-1 mt-1">
                                {itemMatches.slice(0, 4).map((item) => (
                                    <div key={item}>{item}</div>
                                ))}
                                {itemMatches.length > 4 && (
                                    <div className="text-black/30 dark:text-white/30">
                                        +{itemMatches.length - 4} more
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-end">
                    <AnalyzeButton
                        textClassName="text-callout"
                        className="h-fit min-h-fit! p-1.5! min-w-[94px]!"
                    />
                </div>
            </GlobalCardContent>
        </GlobalCard>
    )
}
